import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ColoredRoundActionButton } from "fe-common/ui/coloredRoundActionButton";
import { useAddToHomescreenPrompt } from "./useAddToHomescreenPrompt";
import {
  ACTION,
  CATEGORY,
  evt,
  isCordovaApp,
  isInStandaloneMode,
} from "fe-common/analytics/analytics";
import { useDiagnosticNavigation } from "../components/diagnosticAgent/useDiagnosticNavigation";
import { useDiseasesSections } from "../components/diagnosticAgent/hooks/useDiseasesSections";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { useScreenSize } from "../components/diagnosticAgent/util/useScreenSize";
import { PhonyIphoneBubble } from "./phonyIphoneBubble";
import { useKahunLogger } from "../logging/useKahunLogger";
import {
  UserStorageKeyType,
  useUserStorage,
} from "fe-web-components/userStorage/useUserStorage";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { InstalPopoverView } from "./instalPopoverView";
import { KahunLogo } from "../components/diagnosticAgent/common/kahunLogo";
import { readFromGlobalStorage } from "fe-web-components/userStorage/globalUserStorage";

//region [[ Styles ]]
const Logo = styled(KahunLogo)({
  "& svg": {
    height: "32px",
    fill: KahunColorMap.Primary,
  },
  paddingTop: 0,
  paddingBottom: 0,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export enum AddToHomeScreenModes {
  popover = "popover",
  button = "button",
}

export interface AddToHomeScreenPopoverProps {
  className?: string;
  mode?: AddToHomeScreenModes;
  buttonToRender?: JSX.Element;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]
export const ADD_TO_HOME_TITLE = "Do you like Kahun?";

export const ADD_TO_HOME_TEXT =
  "Get easier access by adding Kahun to your home screen";

const INSTALL_PWA_TEXT = "Install";
//endregion [[ Texts ]]

// Detects if device is on iOS
export const isIos = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

export const isAndroid = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /(android)/i.test(userAgent);
};

export const InstallPWALocalStorageKey = "custom:installPWA";

export const AddToHomeScreenPopover = React.memo(
  ({ mode, buttonToRender, ...props }: AddToHomeScreenPopoverProps) => {
    const [prompt, promptToInstall] = useAddToHomescreenPrompt();
    const [open, setIsOpen] = useState(false);
    // Intentionally mark all as not dismissed for debug services?
    const alwaysShowInstallPwa = getFeatureFlags("alwaysShowInstallPwa");
    const {
      showPwaInstallWindow,
      setShowPwaInstallWindow,
      showIosInstallPwaMessage,
      setShowIosInstallPwaMessage,
    } = useDiagnosticNavigation();

    const { ddData, loading: ddLoading } = useDiseasesSections();

    const { isMobile } = useScreenSize();

    const { info } = useKahunLogger();

    const userStorage = useUserStorage();

    // Since chrome in ios is just a safari with the following user agent:
    // Mozilla/5.0 (iPhone; U; CPU iPhone OS 5_1_1 like Mac OS X; en) AppleWebKit/534.46.0 (KHTML, like Gecko) CriOS/19.0.1084.60 Mobile/9B206 Safari/7534.48.3
    // The ""CriOS"" is the only way to detect it's not a native safari
    //@see: https://stackoverflow.com/questions/13807810/ios-chrome-detection
    const isRunningChrome = useCallback(() => {
      const userAgent = window.navigator.userAgent;
      return userAgent.match("CriOS");
    }, []);

    // Currently (March 2021) iPhone's chrome dons't allow Installing PWAs
    const shouldPromptIos = useMemo(
      () =>
        isIos() &&
        !isInStandaloneMode() &&
        !isRunningChrome() &&
        !isCordovaApp(),
      [isRunningChrome],
    );

    const isPWASupported = useCallback(() => {
      alwaysShowInstallPwa &&
        info("AddToHomeScreenPopover - isPWASupported settings", {
          prompt,
          isMobile,
          shouldPromptIos,
          isInStandaloneMode: isInStandaloneMode(),
        });

      return (prompt || shouldPromptIos) && isMobile;
    }, [prompt, shouldPromptIos, isMobile, info, alwaysShowInstallPwa]);

    useEffect(() => {
      // wait 5 seconds before setting out the message
      if (mode !== AddToHomeScreenModes.button) {
        setTimeout(() => {
          // check again if it was dismissed by the time the timeout ran
          const dismissed =
            !alwaysShowInstallPwa &&
            readFromGlobalStorage(UserStorageKey.InstallPwa);

          if (
            (!dismissed || dismissed === "null") &&
            showPwaInstallWindow &&
            isPWASupported() &&
            !ddLoading &&
            ddData
          ) {
            setIsOpen(true);
            alwaysShowInstallPwa &&
              info("AddToHomeScreenPopover - state settings", {
                isPWASupported: isPWASupported(),
                showPwaInstallWindow,
                ddLoading,
                ddData,
              });
          }
        }, 5000);
      }
    }, [
      mode,
      alwaysShowInstallPwa,
      showPwaInstallWindow,
      isPWASupported,
      ddLoading,
      ddData,
      setIsOpen,
      info,
      userStorage,
    ]);

    const handleInstallClicked = useCallback(() => {
      // Start by permanently hiding the window and preventing it to load again
      setShowPwaInstallWindow(false);
      if (shouldPromptIos && !prompt) {
        setIsOpen(false);
        setShowIosInstallPwaMessage(true);
        return;
      }
      promptToInstall();
      if (!prompt) setIsOpen(false);
      prompt?.userChoice?.then((installResult) => {
        setIsOpen(false);
        userStorage.writeToStorage(
          UserStorageKey.InstallPwa,
          JSON.stringify({
            choice: installResult.outcome,
            date: new Date(),
          }),
          UserStorageKeyType.Global,
        );
        evt({
          category: CATEGORY.PWA_INSTALL_WINDOW,
          action: ACTION.USER_INSTALLED_PWA_RESULT,
          label: installResult.outcome,
        });
      });
    }, [
      setShowPwaInstallWindow,
      setShowIosInstallPwaMessage,
      promptToInstall,
      prompt,
      setIsOpen,
      shouldPromptIos,
      userStorage,
    ]);

    const handleInstallDismissed = useCallback(() => {
      setIsOpen(false);
      userStorage.writeToStorage(
        UserStorageKey.InstallPwa,
        JSON.stringify({
          choice: "dismissed",
          date: new Date(),
        }),
        UserStorageKeyType.Global,
      );
      evt({
        category: CATEGORY.PWA_INSTALL_WINDOW,
        action: ACTION.USER_INSTALLED_PWA_RESULT,
        label: "dismissed by pop-over",
      });
    }, [setIsOpen, userStorage]);

    const handleIosPromptClosed = useCallback(() => {
      setShowIosInstallPwaMessage(false);
      userStorage.writeToStorage(
        UserStorageKey.InstallPwa,
        JSON.stringify({
          choice: "accepted",
          date: new Date(),
        }),
        UserStorageKeyType.Global,
      );
      evt({
        category: CATEGORY.PWA_INSTALL_WINDOW,
        action: ACTION.USER_INSTALLED_PWA_RESULT,
        label: "accepted by ios pop-over",
      });
    }, [setShowIosInstallPwaMessage, userStorage]);

    const handleButtonModeInstallClicked = useCallback(() => {
      // Add an event for clicking on the button
      evt({
        category: CATEGORY.PWA_INSTALL_WINDOW,
        action: ACTION.USER_CLICKED_PWA_FROM_MENU,
        label: "",
      });
      handleInstallClicked();
    }, [handleInstallClicked]);

    const renderButtonMode = useCallback(() => {
      if (buttonToRender) {
        // Inject onclick to handle install
        return React.cloneElement(buttonToRender, {
          onClick: handleButtonModeInstallClicked,
        });
      }
      return (
        <ColoredRoundActionButton
          onClick={handleButtonModeInstallClicked}
          label={INSTALL_PWA_TEXT}
        />
      );
    }, [buttonToRender, handleButtonModeInstallClicked]);

    return (
      <>
        <InstalPopoverView
          visible={open}
          onClose={handleInstallDismissed}
          titleText={ADD_TO_HOME_TITLE}
          contentText={ADD_TO_HOME_TEXT}
        >
          <Logo small preventCLick />
          <ColoredRoundActionButton
            onClick={handleInstallClicked}
            label={INSTALL_PWA_TEXT}
          />
        </InstalPopoverView>
        {/* If button mode and PWA is supported for client's device either render
       a custom button or the default green install button */}
        {mode === AddToHomeScreenModes.button &&
          isPWASupported() &&
          renderButtonMode()}
        {!open && showIosInstallPwaMessage && (
          <PhonyIphoneBubble
            direction={"down"}
            onClose={handleIosPromptClosed}
          />
        )}
      </>
    );
  },
);
